import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { Globe, Copy, CheckCircle2, XCircle, RefreshCcw, ArrowRight, AlertTriangle, ExternalLink, Sparkles, Trash2 } from 'lucide-react';

const STATUS_BADGE = {
    verified: { className: 'bg-emerald-100 text-emerald-800', label: 'Verificado', icon: CheckCircle2 },
    pending: { className: 'bg-amber-100 text-amber-800', label: 'Pendiente', icon: AlertTriangle },
    verifying: { className: 'bg-blue-100 text-blue-800', label: 'Verificando…', icon: RefreshCcw },
    error: { className: 'bg-red-100 text-red-800', label: 'Error', icon: XCircle },
};

const CopyRow = ({ label, value, testId }) => {
    const [copied, setCopied] = useState(false);
    const copy = () => {
        navigator.clipboard.writeText(value);
        setCopied(true);
        toast.success(`${label} copiado`);
        setTimeout(() => setCopied(false), 1500);
    };
    return (
        <div className="flex items-center gap-2">
            <code className="flex-1 px-2 py-1.5 bg-slate-100 rounded font-mono text-xs break-all">{value}</code>
            <Button size="sm" variant="outline" onClick={copy} className="shrink-0" data-testid={testId}>
                {copied ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
            </Button>
        </div>
    );
};

export const DomainWizard = () => {
    const [step, setStep] = useState(1); // 1 domain input, 2 dns instructions, 3 verification
    const [domain, setDomain] = useState('');
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [lastCheck, setLastCheck] = useState(null);

    const load = async () => {
        try {
            const { data } = await api.get('/me/storefront/domain');
            setData(data);
            if (data?.domain) {
                setDomain(data.domain);
                setStep(data.status === 'verified' ? 4 : 2);
            }
        } catch { /* no config yet */ }
    };
    useEffect(() => { load(); }, []);

    const start = async () => {
        const d = domain.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, '');
        if (!/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/.test(d)) {
            toast.error('Dominio inválido. Ejemplo: mitienda.com');
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post('/me/storefront/domain/start', { domain: d });
            setData({ ...data, status: 'pending' });
            setDomain(d);
            setStep(2);
            toast.success('Configuración iniciada. Ahora agrega los registros DNS.');
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); } finally { setBusy(false); }
    };

    const verify = async () => {
        setVerifying(true);
        try {
            const { data: r } = await api.post('/me/storefront/domain/verify');
            setLastCheck(r);
            if (r.ok) {
                toast.success('¡Dominio verificado!');
                setStep(4);
                await load();
            } else {
                toast.info('DNS aún no listo. Revisa los registros e intenta de nuevo.');
                setStep(3);
            }
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); } finally { setVerifying(false); }
    };

    const reset = async () => {
        if (!window.confirm('¿Desconectar dominio? Podrás configurarlo de nuevo después.')) return;
        try {
            await api.delete('/me/storefront/domain');
            setData(null); setDomain(''); setLastCheck(null); setStep(1);
            toast.success('Dominio desconectado');
        } catch { toast.error('Error'); }
    };

    const records = data?.dns_instructions?.records || [];
    const currentStatus = data?.status || (step === 4 ? 'verified' : 'pending');
    const badge = STATUS_BADGE[currentStatus] || STATUS_BADGE.pending;
    const StatusIcon = badge.icon;

    const totalSteps = 4;
    const progress = ((step) / totalSteps) * 100;

    return (
        <Card data-testid="domain-wizard">
            <CardContent className="p-5 space-y-4">
                {/* Header */}
                <div className="flex items-center gap-3 flex-wrap">
                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center shrink-0">
                        <Globe className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold">Dominio personalizado</h3>
                            {data?.domain && (
                                <Badge className={badge.className}>
                                    <StatusIcon className={`h-3 w-3 mr-1 ${currentStatus === 'verifying' ? 'animate-spin' : ''}`} />
                                    {badge.label}
                                </Badge>
                            )}
                        </div>
                        <p className="text-xs text-muted-foreground">Conecta tu propio dominio en 4 pasos, con instrucciones DNS claras.</p>
                    </div>
                    <a href="https://okgo.mx/docs/dominio" target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1">
                        Guía completa <ExternalLink className="h-3 w-3" />
                    </a>
                </div>

                {/* Progress + step indicators */}
                <div>
                    <Progress value={progress} className="h-1.5" />
                    <div className="mt-2 grid grid-cols-4 gap-1 text-[10px] uppercase tracking-wider">
                        {['Dominio', 'DNS', 'Verificar', '¡Listo!'].map((label, i) => (
                            <div key={i} className={`flex items-center gap-1 ${step >= i + 1 ? 'text-foreground font-semibold' : 'text-muted-foreground'}`} data-testid={`wizard-step-label-${i + 1}`}>
                                <span className={`h-4 w-4 rounded-full flex items-center justify-center text-[9px] ${step > i + 1 ? 'bg-emerald-500 text-white' : step === i + 1 ? 'bg-primary text-white' : 'bg-slate-200 text-slate-500'}`}>{step > i + 1 ? '✓' : i + 1}</span>
                                {label}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Step 1: Enter domain */}
                {step === 1 && (
                    <div className="space-y-3" data-testid="wizard-step-1">
                        <div>
                            <Label>Tu dominio</Label>
                            <div className="flex gap-2 mt-1">
                                <Input
                                    value={domain}
                                    onChange={(e) => setDomain(e.target.value)}
                                    placeholder="mitienda.com"
                                    onKeyDown={(e) => e.key === 'Enter' && start()}
                                    data-testid="wizard-domain-input"
                                />
                                <Button onClick={start} disabled={busy || !domain.trim()} data-testid="wizard-start-btn">
                                    {busy ? 'Configurando…' : 'Continuar'} <ArrowRight className="h-4 w-4 ml-1" />
                                </Button>
                            </div>
                            <p className="text-[10px] text-muted-foreground mt-1">Sin <code>https://</code>, sin <code>www.</code> Ejemplo: <code>mitienda.com</code></p>
                        </div>
                        <Alert>
                            <Sparkles className="h-4 w-4" />
                            <AlertDescription className="text-xs">
                                ¿No tienes dominio? Consíguelo en Namecheap, GoDaddy o Google Domains desde $150 MXN/año.
                            </AlertDescription>
                        </Alert>
                    </div>
                )}

                {/* Step 2 & 3: DNS instructions + verify */}
                {(step === 2 || step === 3) && (
                    <div className="space-y-3" data-testid={`wizard-step-${step}`}>
                        <div className="p-3 rounded bg-slate-50 border">
                            <div className="text-sm">Dominio a configurar: <strong className="text-primary" data-testid="wizard-domain-display">{data?.domain || domain}</strong></div>
                        </div>

                        <div>
                            <h4 className="text-sm font-semibold mb-1">Agrega estos 3 registros en tu proveedor DNS:</h4>
                            <p className="text-xs text-muted-foreground mb-3">
                                Ve al panel DNS de tu registrador (Namecheap, GoDaddy, Cloudflare…) y crea los siguientes registros:
                            </p>
                            <div className="border rounded-lg overflow-hidden">
                                <table className="w-full text-xs">
                                    <thead className="bg-slate-100 text-[10px] uppercase text-muted-foreground">
                                        <tr>
                                            <th className="p-2 text-left">Tipo</th>
                                            <th className="p-2 text-left">Nombre</th>
                                            <th className="p-2 text-left">Valor</th>
                                            <th className="p-2 text-left">TTL</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {records.map((r, i) => (
                                            <tr key={i} className="border-t align-top" data-testid={`wizard-dns-row-${r.type.toLowerCase()}`}>
                                                <td className="p-2 font-mono font-semibold"><Badge variant="outline" className="text-[10px]">{r.type}</Badge></td>
                                                <td className="p-2 font-mono">
                                                    <CopyRow label={`Nombre ${r.type}`} value={r.name} testId={`copy-name-${r.type}`} />
                                                </td>
                                                <td className="p-2 font-mono">
                                                    <CopyRow label={`Valor ${r.type}`} value={r.value} testId={`copy-value-${r.type}`} />
                                                </td>
                                                <td className="p-2 text-muted-foreground">{r.ttl}s</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="text-[10px] text-muted-foreground mt-2 space-y-0.5">
                                {records.map((r, i) => (
                                    <div key={i}><strong>{r.type}:</strong> {r.hint}</div>
                                ))}
                            </div>
                        </div>

                        <Alert className="border-amber-200 bg-amber-50">
                            <AlertTriangle className="h-4 w-4" />
                            <AlertDescription className="text-xs">
                                {data?.dns_instructions?.propagation_note || 'Los cambios DNS pueden tardar hasta 24 h en propagarse.'}
                            </AlertDescription>
                        </Alert>

                        <div className="flex flex-wrap gap-2 pt-2 border-t">
                            <Button onClick={verify} disabled={verifying} data-testid="wizard-verify-btn" size="sm">
                                <RefreshCcw className={`h-4 w-4 mr-1 ${verifying ? 'animate-spin' : ''}`} />{verifying ? 'Verificando…' : 'Verificar DNS ahora'}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={reset} className="text-destructive hover:text-destructive" data-testid="wizard-reset-btn"><Trash2 className="h-4 w-4 mr-1" />Cancelar</Button>
                        </div>

                        {lastCheck && !lastCheck.ok && (
                            <div className="p-3 rounded border border-amber-200 bg-amber-50 space-y-1.5" data-testid="wizard-verify-errors">
                                <div className="text-sm font-semibold text-amber-800 flex items-center gap-1"><AlertTriangle className="h-4 w-4" />DNS aún no listo</div>
                                <div className="flex gap-3 text-xs">
                                    <div className={`flex items-center gap-1 ${lastCheck.checks?.txt_ok ? 'text-emerald-700' : 'text-red-700'}`}>{lastCheck.checks?.txt_ok ? '✓' : '✗'} TXT</div>
                                    <div className={`flex items-center gap-1 ${lastCheck.checks?.a_ok ? 'text-emerald-700' : 'text-red-700'}`}>{lastCheck.checks?.a_ok ? '✓' : '✗'} A</div>
                                    <div className={`flex items-center gap-1 ${lastCheck.checks?.cname_ok ? 'text-emerald-700' : 'text-red-700'}`}>{lastCheck.checks?.cname_ok ? '✓' : '✗'} CNAME</div>
                                </div>
                                {(lastCheck.checks?.errors || []).length > 0 && (
                                    <ul className="text-[10px] text-amber-900 list-disc pl-5">
                                        {lastCheck.checks.errors.slice(0, 3).map((err, i) => <li key={i}>{err}</li>)}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Step 4: verified */}
                {step === 4 && (
                    <div className="p-4 rounded-xl border-2 border-emerald-200 bg-emerald-50 space-y-2" data-testid="wizard-step-4">
                        <div className="flex items-center gap-2">
                            <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                            <div>
                                <div className="font-bold text-emerald-800">¡Tu dominio está conectado!</div>
                                <div className="text-xs text-emerald-700">Tu tienda está disponible en: <a href={`https://${data?.domain}`} target="_blank" rel="noreferrer" className="underline font-mono">{data?.domain}</a></div>
                            </div>
                        </div>
                        {data?.verified_at && (
                            <div className="text-[10px] text-emerald-700/80">Verificado: {new Date(data.verified_at).toLocaleString('es-MX')}</div>
                        )}
                        <div className="flex gap-2 pt-1">
                            <Button size="sm" variant="outline" asChild><a href={`https://${data?.domain}`} target="_blank" rel="noreferrer"><ExternalLink className="h-3.5 w-3.5 mr-1" />Abrir tienda</a></Button>
                            <Button size="sm" variant="ghost" onClick={reset} className="text-destructive hover:text-destructive"><Trash2 className="h-3.5 w-3.5 mr-1" />Desconectar</Button>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default DomainWizard;
